import * as React from 'react';

import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { navigation } from '../../config/routes';

interface Props {
  title?: string;
  description?: string;
  pathname?: string;
  article?: boolean;
  dateISO?: string;
  endDateISO?: string;
  authors?: readonly Queries.authorFieldsFragment[];
  cover?: string | null;
  socialCard?: string | null;
  page?: string;
  address?: string;
  eventType?: 'talk' | 'podcast';
  url?: string;
  tags?: CustomTypes.Tags;
}

const toTag = (data: any) => {
  return (
    <script
      key={`${data['@type']}-${data.name}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(data),
      }}
    ></script>
  );
};

export const JSONLD = ({
  title,
  description,
  cover,
  socialCard,
  pathname,
  article,
  dateISO,
  endDateISO,
  authors,
  page,
  address,
  url,
  tags,
  eventType,
}: Props) => {
  const {
    author,
    title: defaultTitle,
    description: defaultDescription,
    name,
    image,
    logo,
    siteUrl,
    twitterUsername,
    foundingDate,
    twitter,
    facebook,
    instagram,
    linkedin,
    github,
    dribbble,
    email,
  } = useSiteMetadata();

  const seo = {
    author,
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image}`,
    logo: `${siteUrl}${logo}`,
    url: `${siteUrl}${pathname || ''}`,
    twitterUsername,
    name,
    foundingDate,
    twitter,
    facebook,
    instagram,
    linkedin,
    github,
    dribbble,
    email,
  };

  const datePublished = dateISO && dateISO.split('T')[0];
  const endDate = endDateISO && endDateISO.split('T')[0];

  const seoImage = socialCard || cover || seo.image;

  const personAuthors = (authors || []).map((author) => ({
    '@type': 'Person',
    name: author.name,
    jobTitle: author.about,

    ...(author.github || author.twitter
      ? {
          url:
            author.github ||
            `https://twitter.com/${(author.twitter || '').replace('@', '')}`,
        }
      : {}),
  }));

  return (
    <>
      {[
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          url: siteUrl,
          name: seo.author,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Brooklyn',
            addressRegion: 'New York',
            postalCode: '11201',
            streetAddress: '77 Sands St.',
          },
          email: `mailto:${seo.email}`,
          telephone: '+1 888 400 5485',
          foundingDate: seo.foundingDate,
          image: seo.image,
          logo: seo.logo,
          sameAs: [
            seo.twitter,
            seo.facebook,
            seo.instagram,
            seo.linkedin,
            seo.github,
            seo.dribbble,
          ],
        },
        pathname && pathname.length > 1
          ? {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: pathname
                .split('/')
                .filter(Boolean)
                .reduce(
                  (acc: string[], pathkey: string) => [
                    ...acc,
                    acc.join('/') + '/' + pathkey,
                  ],
                  [],
                )
                .map((pathkey, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  name: (
                    navigation.find((nav) => nav.url === pathkey) || {
                      label: title,
                    }
                  ).label,

                  item: `${siteUrl}${pathkey}`,
                })),
            }
          : false,
        article
          ? {
              '@context': 'https://schema.org',
              '@type': page === 'post' ? 'BlogPosting' : 'Article',
              headline: seo.title,
              name: seo.title,
              description: seo.description,
              image: [seoImage],
              ...(datePublished ? { datePublished: datePublished } : {}),
              ...(authors && authors.length > 0
                ? {
                    author: personAuthors,
                  }
                : {}),
              publisher: {
                '@type': 'Organization',
                name: seo.author,
                logo: {
                  '@type': 'ImageObject',
                  url: seo.logo,
                },
              },
            }
          : false,
        page === 'event' && eventType === 'talk'
          ? {
              '@context': 'https://schema.org',
              '@type': 'Event',
              name: seo.title,
              description: seo.description,
              ...(address ? { location: address } : {}),
              eventStatus: 'https://schema.org/EventScheduled',
              startDate: datePublished,
              endDate: endDate || datePublished,
              ...(url ? { url } : {}),
              image: [seoImage],
              ...(tags && tags.length > 0
                ? {
                    keywords: tags.map((tag) => tag.label).join(', '),
                  }
                : {}),
              ...(authors && authors.length > 0
                ? {
                    performer: personAuthors,
                  }
                : {}),
              contributor: {
                '@type': 'Organization',
                name: seo.author,
                logo: {
                  '@type': 'ImageObject',
                  url: seo.logo,
                },
              },
            }
          : false,
        ...(page === 'devpropulsionlabs' && authors && authors.length > 0
          ? personAuthors
          : []),
        (page === 'event' || page === 'devpropulsionlabs') &&
        eventType === 'podcast'
          ? {
              '@context': 'https://schema.org',
              '@type': 'MediaObject',
              name: seo.title,
              description: seo.description,
              dateCreated: datePublished,
              datePublished: datePublished,
              ...(url ? { url } : {}),
              image: [seoImage],
              ...(tags && tags.length > 0
                ? {
                    keywords: tags.map((tag) => tag.label).join(', '),
                  }
                : {}),
              ...(authors && authors.length > 0
                ? {
                    author: personAuthors,
                  }
                : {}),
              contributor: {
                '@type': 'Organization',
                name: seo.author,
                logo: {
                  '@type': 'ImageObject',
                  url: seo.logo,
                },
              },
            }
          : false,
      ]
        .filter(Boolean)
        .map(toTag)}
    </>
  );
};
